import { createTheme } from '@mui/material/styles';
import colors, { palette as figmaPalette } from './tokens/colors';
import { figmaTokenCss } from './tokens/cssVariables';
import { fontFamily } from './tokens/typography';

/** Existing app palette — kept to avoid visual regressions on live screens */
const legacyPalette = {
  primary: {
    main: '#181B28',
  },
  background: {
    default: '#FBFBFB',
    paper: '#FBFBFB',
  },
  text: {
    primary: '#1B2226',
    secondary: '#555',
  },
};

/** Global baseline CSS — unchanged from original App.jsx setup */
const cssBaselineOverrides = `
  /* Keep one vertical scroll on <html>; avoids body scroll-lock after MUI overlays */
  html { overflow-x: hidden; overflow-y: scroll; }
  body { min-height: 100%; }
  #root { min-height: 100%; }
  /* GLOBAL TAP HIGHLIGHT REMOVE */
  * {
    -webkit-tap-highlight-color: transparent;
  }
  button, a, div {
    -webkit-tap-highlight-color: transparent;
  }
  /*
   * WebKit/Chrome autofill: remove default blue/yellow highlight app-wide.
   * Uses design tokens (--yovo-grey-200 shell, --yovo-base-black text).
   */
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill,
  .MuiAutocomplete-input:-webkit-autofill,
  input:-webkit-autofill,
  textarea:-webkit-autofill,
  select:-webkit-autofill,
  .MuiOutlinedInput-input:-webkit-autofill:hover,
  .MuiInputBase-input:-webkit-autofill:hover,
  .MuiAutocomplete-input:-webkit-autofill:hover,
  input:-webkit-autofill:hover,
  textarea:-webkit-autofill:hover,
  select:-webkit-autofill:hover,
  .MuiOutlinedInput-input:-webkit-autofill:focus,
  .MuiInputBase-input:-webkit-autofill:focus,
  .MuiAutocomplete-input:-webkit-autofill:focus,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill:focus,
  .MuiOutlinedInput-input:-webkit-autofill:active,
  .MuiInputBase-input:-webkit-autofill:active,
  .MuiAutocomplete-input:-webkit-autofill:active,
  input:-webkit-autofill:active,
  textarea:-webkit-autofill:active,
  select:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--yovo-grey-200, #e8e9ed) inset !important;
    box-shadow: 0 0 0 1000px var(--yovo-grey-200, #e8e9ed) inset !important;
    -webkit-text-fill-color: var(--yovo-base-black, #20202a) !important;
    caret-color: var(--yovo-base-black, #20202a) !important;
    transition: background-color 99999s ease-out 0s;
  }
  ${figmaTokenCss()}
`;

/**
 * Phase 1 theme — typography + colors only (no shape, spacing, or shadows).
 * Legacy palette preserved; Figma tokens available via theme.palette.tokens.
 * radius, spacing, shadows remain import-only from theme/tokens/.
 */
export function createAppTheme(options = {}) {
  return createTheme({
    palette: {
      ...legacyPalette,
      error: figmaPalette.error,
      warning: figmaPalette.warning,
      success: figmaPalette.success,
      info: figmaPalette.info,
      /** Full Figma color library: theme.palette.tokens.neutral[900] etc. */
      tokens: colors,
    },
    typography: {
      fontFamily: fontFamily.primary,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: cssBaselineOverrides,
      },
    },
    ...options,
  });
}

const appTheme = createAppTheme();

export default appTheme;
